import { useEffect, useState } from "react";
import { toast } from "sonner";

/** Opens an accounting provider's OAuth page in a new window (never with noopener — that drops the login session
 *  inside the preview frame) and polls `getStatus` until the connection appears. */
export const useOauthWindow = ({ label, getLink, getStatus, onConnected }) => {
  const [waiting, setWaiting] = useState(false);
  const [startedAt, setStartedAt] = useState(null);

  const open = async () => {
    let url;
    try {
      url = await getLink();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not start ${label} sign-in`);
      return;
    }
    const win = window.open(url, `d20-${label}-oauth`, "popup=yes,width=620,height=760");
    if (!win) {
      if (window.top !== window.self) {
        toast.error(`Your browser blocked the ${label} window. Open D20 in its own tab, then try again.`);
        return;
      }
      window.location.href = url;
      return;
    }
    setStartedAt(new Date().toISOString());
    setWaiting(true);
    toast.info(`Finish signing in to ${label} in the new window — this page updates automatically.`);
  };

  useEffect(() => {
    if (!waiting) return;
    let ticks = 0;
    const t = setInterval(async () => {
      ticks += 1;
      try {
        const s = await getStatus();
        if (s.connected && (!s.connected_at || !startedAt || s.connected_at >= startedAt)) {
          setWaiting(false);
          onConnected?.(s);
          return;
        }
      } catch (e) { console.debug("status poll failed", e); }
      if (ticks > 100) {
        setWaiting(false);
        toast.error(`Timed out waiting for ${label} — click Connect to try again.`);
      }
    }, 3000);
    return () => clearInterval(t);
  }, [waiting, startedAt, getStatus, onConnected, label]);

  return { open, waiting };
};
